import type { ContainerFormat, EncodedAudioChunkLike } from './AudioExport.js';
import type { EncodedVideoChunkLike, VideoCodecCandidate } from './VideoExport.js';
import type { AudioCodecCandidate } from './AudioExport.js';

/**
 * The container, as the core sees it.
 *
 * Muxing is the third thing that could have been written inside the encoders
 * and should not be. An encoder that also writes boxes cannot be tested without
 * producing a file, cannot be swapped for a different container, and grows a
 * dependency on a muxing library in the one place that has to stay close to the
 * platform. So it is its own adapter, with the same shape as the other two: the
 * core drives it, the host implements it, and everything format-specific — the
 * `moov` layout, the Matroska clusters, the codec-private bytes — is on the far
 * side of this interface.
 */

export interface MuxerTrackConfig {
  readonly width: number;
  readonly height: number;
  readonly framerate: number;
}

/**
 * A container being written.
 *
 * Ordering is not negotiable and is the reason `finalize` is separate from
 * `dispose`: chunks may only be added between `start` and `finalize`, both
 * encoders must have been flushed before `finalize` is called, and the file
 * does not exist until `finalize` resolves. Calling `dispose` on a container
 * that was never finalized is how a cancelled export is supposed to end —
 * see `abort`.
 */
export interface MuxerAdapter {
  readonly container: ContainerFormat;
  /** Opens the file. Tracks are fixed at this point. */
  start(): Promise<void>;
  /**
   * Adds one encoded video chunk, in decode order.
   *
   * Awaited: a muxer writing to a slow target signals backpressure by taking
   * longer to resolve, and an export that ignored that would buffer the whole
   * file in memory to keep a loop tidy.
   */
  addVideoChunk(chunk: EncodedVideoChunkLike): Promise<void>;
  addAudioChunk(chunk: EncodedAudioChunkLike): Promise<void>;
  /** Closes the file and returns its bytes. */
  finalize(): Promise<Uint8Array>;
  /**
   * Abandons the file.
   *
   * A cancelled export must not produce something that looks finished. An MP4
   * whose `moov` was written is indistinguishable from a complete one to every
   * player that opens it, so a cancelled export has to stop *before* that,
   * and `abort` is where that decision lives.
   */
  abort(): Promise<void>;
  dispose(): void;
}

// ---------------------------------------------------------------------------
// Choosing a container
// ---------------------------------------------------------------------------

export interface ContainerChoice {
  readonly container: ContainerFormat;
  readonly video: VideoCodecCandidate;
  readonly audio: AudioCodecCandidate | null;
  readonly reason: string;
}

/**
 * Picks the container from the codecs, not the other way round.
 *
 * The tempting shortcut is to choose the container from the video codec alone —
 * H.264 means MP4, VP9 means WebM — and it produces an unplayable file the first
 * time the audio disagrees. The combination is what has to be legal: MP4 will
 * not carry VP9 in any way worth shipping, WebM will not carry AAC at all, and a
 * muxer that accepts a track it cannot describe writes a header that no
 * demuxer will read back.
 *
 * Phase 7A already showed why this cannot be decided in advance: a 1920×1920
 * export found no H.264 encoder on this machine and fell back to VP9, which
 * silently changes the right container underneath. So the choice is made from
 * whatever the probes actually returned.
 */
export function chooseContainer(options: {
  readonly video: VideoCodecCandidate;
  readonly audio: AudioCodecCandidate | null;
  readonly muxerSupports?: (container: ContainerFormat) => boolean;
}): ContainerChoice | null {
  const supported = options.muxerSupports ?? ((): boolean => true);
  const { video, audio } = options;

  if (!supported(video.container)) return null;

  if (audio === null) {
    return {
      container: video.container,
      video,
      audio: null,
      reason: `${video.name} with no audio track`,
    };
  }

  if (audio.containers.includes(video.container)) {
    return {
      container: video.container,
      video,
      audio,
      reason: `${video.name} + ${audio.name} in ${video.container.toUpperCase()}`,
    };
  }

  // The codecs disagree. Reported rather than resolved: silently dropping the
  // audio track would answer the wrong question, and re-picking the video codec
  // belongs to the caller that ran the probes.
  return null;
}

// ---------------------------------------------------------------------------
// What came out
// ---------------------------------------------------------------------------

/** Everything a caller should be able to learn about a finished file. */
export interface MuxedFileInfo {
  readonly container: ContainerFormat;
  readonly mimeType: string;
  readonly byteLength: number;
  readonly videoCodec: string | null;
  readonly audioCodec: string | null;
  readonly width: number | null;
  readonly height: number | null;
  readonly fps: number | null;
  readonly videoDuration: number | null;
  readonly audioDuration: number | null;
  readonly sampleRate: number | null;
  readonly channels: number | null;
}
